/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  useClickstreamCallback,
  useModalDisplayedEvent
} from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import PageLoading from '@zenbusiness/zui/PageLoading';

import ResumeModal from '#client/components/ResumeModal';
import { ENABLE_RESUME_MODAL } from '#client/constants/experimentFlags';
import useFlowConfig from '#client/hooks/flow/useFlowConfig';
import useFlowState from '#client/hooks/flow/useFlowState';
import * as pageUtils from '#client/utils/flowPage';

/*
  Things Flow Page Gate component must enforce:

  1. If lastPage is invalid, redirect to the start of the flow.
  2. If flow page is invalid, redirect to the progress page.
  3. If flow page is ahead of the progress page, redirect to the progress page.
  
  Update flowState lastPage if it is invalid
*/

export default function FlowPageGate({ children }) {
  const { flowPage } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { id: flowId, pages } = useFlowConfig();
  const { lastPage, set } = useFlowState();
  const trackFlowStarted = useClickstreamCallback('flowStarted');
  const trackFlowResumed = useClickstreamCallback('flowResumed');
  const [displayResumeModal, setDisplayResumeModal] = useState(false);
  const enableResumeModal = useExperimentFlag(ENABLE_RESUME_MODAL, false);

  const isPageValid = pageUtils.isPageValid(pages, flowPage);
  const isLastPageValid = pageUtils.isPageValid(pages, lastPage);
  const isPageAheadOfProgress =
    isPageValid &&
    isLastPageValid &&
    pageUtils.getPageIndex({ pages, page: flowPage }) >
      pageUtils.getPageIndex({ pages, page: lastPage });
  const firstPage = pageUtils.getFirstPage(pages);

  useModalDisplayedEvent({
    open: displayResumeModal,
    elementName: 'flow_resume_modal'
  });

  // Navigates to a specific page in the flow
  const navigateToPage = (pagePath) => {
    navigate(pagePath, { replace: true });
  };

  const handleOnResume = () => {
    trackFlowResumed();
    setDisplayResumeModal(false);
    const lastPagePath = pageUtils.getPagePath({ flowId, page: lastPage });
    navigateToPage(lastPagePath);
  };

  const handleOnRestart = () => {
    trackFlowStarted();
    setDisplayResumeModal(false);
    const firstPagePath = pageUtils.getPagePath({ flowId, page: firstPage });
    navigateToPage(firstPagePath);
  };

  // Handle incoming redirect from FlowEntry that triggers events
  useEffect(() => {
    const currentPagePath = pageUtils.getPagePath({ flowId, page: flowPage });

    if (searchParams.has('resumed')) {
      if (enableResumeModal) {
        setDisplayResumeModal(true);
      } else {
        trackFlowResumed();
      }
      navigateToPage(currentPagePath);
    } else if (searchParams.has('started')) {
      trackFlowStarted();
      navigateToPage(currentPagePath);
    }
  }, [flowId, flowPage, navigate, searchParams, trackFlowResumed, trackFlowStarted]);

  // If the lastPage is invalid, redirect to first page of the flow
  useEffect(() => {
    if (!isLastPageValid) {
      set('lastPage', firstPage);
      const firstPagePath = pageUtils.getPagePath({ flowId, page: firstPage });
      navigateToPage(firstPagePath);
    }
  }, [firstPage, flowId, isLastPageValid, navigate, pages, set]);

  // If the page is invalid, redirect to the lastPage
  useEffect(() => {
    if (!isPageValid && isLastPageValid) {
      const lastPagePath = pageUtils.getPagePath({ flowId, page: lastPage });
      navigateToPage(lastPagePath);
    }
  }, [flowId, isLastPageValid, isPageValid, navigate, lastPage]);

  // If the page is ahead of the lastPage, redirect to the lastPage
  useEffect(() => {
    if (isPageAheadOfProgress) {
      const lastPagePath = pageUtils.getPagePath({ flowId, page: lastPage });
      navigateToPage(lastPagePath);
    }
  }, [flowId, isPageAheadOfProgress, lastPage, navigate]);

  if (!isPageValid || !isLastPageValid || isPageAheadOfProgress) {
    return <PageLoading />;
  }

  return (
    <>
      <ResumeModal
        open={displayResumeModal}
        onRestart={handleOnRestart}
        onResume={handleOnResume}
      />
      {children || <Outlet />}
    </>
  );
}
